import React, { useState, useEffect } from 'react';
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Grid from "@mui/material/Grid";
import { useDispatch, useSelector } from 'react-redux';
import GPAP_Dialog from '../../../gpap-lib/components/GPAP_Dialog';
import axios from 'axios';
import { useLocation } from 'react-router-dom';
import {analysisTypes} from "./configDirOverview";
import CaButton from '../../../gpap-lib/components/shared-components/CaButton';
import { setTable } from '../../../reducers/slices/tables/table.slice';
import GeneralInfo from './components/GeneralInfo';
import AdministrativeInfo from './components/AdministrativeInfo';
import FiltersSection from './components/FilterSection';
import ControlSampleAndExperiment from './components/ControlSampleExperiment';
import GermlineSampleAndExperiment from './components/GermlineSampleExperiment';
import TumorSampleAndExperiment from './components/TumorSampleExperiment';
import PatientInfo from './components/PatientInfo';
import ClinicalInfo from './components/ClinicalInfo';
import Observations from './components/Observations';
import { useTranslation } from 'react-i18next';
import i18next from 'i18next';

    export default function DIR_Overview() {
        const { t, i18n } = useTranslation();
        const location = useLocation();
        const hideFooter = !location.pathname.includes("/new_clinical_report");
        const token = useSelector(state => state.authorization.token);
        const dispatch = useDispatch();
        const {allDataApis } = useSelector((state) => state.generaltable);

        const [selectedStatus, setSelectedStatus] = useState('');
        const [selectedPriority, setSelectedPriority] = useState('');

        const [sampleQualityObservations, setSampleQualityObservations] = useState('');
        const [sequencingQualityObservations, setSequencingQualityObservations] = useState('');
        const selectedDir = allDataApis?.dirs;

        const [openDialog, setDialog] = useState({
            status: false,
            title: "",
            text: "",
            open: false,
            type: "success"
        });

        const [formValues, setFormValues] = useState({
            filter_set: "",
            predefined_list: "",
        });

        const [filterSetList, setFilterSetList] = useState([]);
        const [predefinedGeneList, setPredefindedGeneList] = useState([]);

        useEffect(() => {
            if (selectedDir) {
                setSelectedStatus(selectedDir.status || '');
                setSelectedPriority(selectedDir.priority || '');
                setSampleQualityObservations(selectedDir.sample_quality_observations || '');
                setSequencingQualityObservations(selectedDir.sequencing_quality_observations || '');
                setFormValues({
                    filter_set: selectedDir.filter_set ? selectedDir.filter_set.toString() : "",
                    predefined_list: selectedDir.predefined_list ? selectedDir.predefined_list.toString() : ""
                });
            }
        }, [selectedDir]);

        const handleChange = (event) => {
            setSelectedStatus(event.target.value);
        };

        const handleChangePriority = (event) => {
            setSelectedPriority(event.target.value);
        }


        // Query possible values for filterSetList and Predefined Gene List
        useEffect(() => {
            // Get Filter Set Options
            const url = window.config.apis_configuration.api_nextgpap_server;
            fetch(url+ '/filter_set', {
                method: 'GET',
                headers: { 'Content-Type': 'application/json', "Authorization": token}
            })
            .then((response) => response.json())
            .then((data) => {
                setFilterSetList(data.data.filter(s => s.filter_set_name!=="").map(fs => ({name: fs.filter_set_name, id: fs.id.toString()})));
            })
            .catch((error) => {
                setDialog({
                    status: false, open: true, type: "error",
                    title: "Could not get filter set list",
                    text: "Broken connection to back-end API. Please try again later."
                });
            });
        }, []);

        // After Having the filter set loaded then require the gene list
        useEffect(() => {
            const url = window.config.apis_configuration.api_nextgpap_server;
            fetch( url + '/get_all_lists', {
                method: 'GET', headers: { 'Content-Type': 'application/json', "Authorization": token }
            })
            .then((response) => response.json())
            .then((data) => {
                setPredefindedGeneList(data.data.map(gl => ({name: gl.title, id: gl.id.toString()})));
            })
            .catch((error) => {
                setDialog({
                    status: false, open: true, type: "error",
                    title: "Could not get predefined gene lists",
                    text: "Broken connection to back-end API. Please try again later.",
                });
            });
        }, []);
        

        const handleInputChange = (event) => {
            const { name, value } = event.target;
            if (name === "sampleQualityObservations") {
                setSampleQualityObservations(value);
            } else if (name === "sequencingQualityObservations") {
                setSequencingQualityObservations(value);
            }
            else {
                setFormValues({ ...formValues, [name]: value });
            }
        };

        const updateDir = () => {
            const payload = { ...formValues };
            const dirId = allDataApis?.dirs?.dir_id;
            const url = window.config.apis_configuration.api_nextgpap_server;
            fetch( url+ '/dir/' + dirId, {
                method: 'PUT', headers: { 'Content-Type': 'application/json', "Authorization": token },
                body: JSON.stringify(payload),
            })
            .then((response) => response.json())
            .then((data) => {
                setDialog({ status: true, open: true, type: "success",
                    title: "Request " +  dirId +  " updated successfully",
                    text: "Request " + dirId + " has been properly updated."
                });
            })
            .catch((error) => {
                console.error('Error:', error);
                setDialog({ status: false, open: true, type: "error",
                    title: "Error updating request",
                    text: "Backend connection broken:",
                });
            });
        };
        const handleSave = async () => {
            let payload = {
                status: selectedStatus,
                priority: selectedPriority,
                sample_quality_observations: sampleQualityObservations,
                sequencing_quality_observations: sequencingQualityObservations,
                filter_set: formValues.filter_set ? parseInt(formValues.filter_set, 10) : null,
                predefined_list: formValues.predefined_list ? parseInt(formValues.predefined_list, 10) : null,
                deadline: selectedDir?.deadline
            };
        
            const analysisType = selectedDir?.analysis;
            if (analysisType === "germline") {
                payload = {
                    ...payload,
                    control_experiment_id: selectedDir?.control_experiment_id || "",
                    tumor_experiment_id: "",
                };
            } else if (analysisType === "somatic_tumor_only" || analysisType === "somatic_fusion_analysis") {
                payload = {
                    ...payload,
                    control_experiment_id: "",
                    tumor_experiment_id: selectedDir?.tumor_experiment_id || "",
                };
            } else if (analysisType === "somatic_tumor_normal") {
                payload = {
                    ...payload,
                    control_experiment_id: selectedDir?.control_experiment_id || "",
                    tumor_experiment_id: selectedDir?.tumor_experiment_id || "",
                };
            }
        
            const dirId = selectedDir?.dir_id;
            const url = window.config.apis_configuration.api_nextgpap_server;
            try {
                const response = await axios.put(`${url}/dir/${dirId}`, payload, {
                    headers: { 
                        'Content-Type': 'application/json', 
                        'Authorization': token 
                    }
                });
        
                if (response.status === 200 || response.status === 204) {
           
                    const updatedDirResponse = await axios.get(`${url}/dir/${dirId}`, {
                        headers: { 
                            'Content-Type': 'application/json', 
                            'Authorization': token 
                        }
                    });
                    const updatedDir = updatedDirResponse.data.item;
        
                    dispatch(setTable({
                        option: 'allDataApis',
                        value: {
                            ...allDataApis,
                            dirs: updatedDir
                        }
                    }));
        
                    setDialog({
                        status: true,
                        open: true,
                        type: "success",
                        title: t("Request updated successfully"),
                        text: t("The request has been updated successfully.")
                    });
                    
                    setSampleQualityObservations(updatedDir.sample_quality_observations);
                    setSequencingQualityObservations(updatedDir.sequencing_quality_observations);
        
                } else {
                    throw new Error('Failed to update request');
                }
        
            } catch (error) {
                console.error('Error:', error);
                setDialog({
                    status: false,
                    open: true,
                    type: "error",
                    title: "Error updating request",
                    text: "An error occurred while updating the request."
                });
            }
        };

        const controlSample= allDataApis?.experiments?.[1];
        
        
        const analysisType = useSelector(state => state.generaltable.allDataApis?.dirs?.analysis);


        const analysisTypeLabel = analysisTypes.find(item => item.value === analysisType)?.label || analysisType || "NA";


        const handleCloseDialog = () => { setDialog({ status: false, title: "", text: "", open: false,type: "success"});}

        const renderDialog = () => {
            return <GPAP_Dialog
                open={openDialog.open}
                handleClose={handleCloseDialog}
                title={t(openDialog.title)}
                text={t(openDialog.text)}
                onExitText={false}
                onConfirmText={t("Ok")}
                onExitAction={false}
                onConfirmAction={handleCloseDialog}
                type={openDialog.type}
            />
        }

        return (    
            <Box sx={{ flexGrow: 1, p: 2 }}>
            {renderDialog()}
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <Box>
                            <GeneralInfo
                                allDataApis={allDataApis}
                                selectedDir={selectedDir}
                                analysisTypeLabel={analysisTypeLabel}
                                selectedStatus={selectedStatus}
                                handleChangeStatus={handleChange}
                                selectedPriority={selectedPriority}
                                handleChangePriority={handleChangePriority}
                            />
                            <AdministrativeInfo allDataApis={allDataApis} />
                            <FiltersSection
                                formValues={formValues}
                                handleInputChange={handleInputChange}
                                filterSetList={filterSetList}
                                predefinedGeneList={predefinedGeneList}
                            />
                            <PatientInfo allDataApis={allDataApis} />
                            <ClinicalInfo allDataApis={allDataApis} />
                            
                            {(analysisType !== "germline") && (
                                <TumorSampleAndExperiment
                                    selectedDir={selectedDir}
                                    allDataApis={allDataApis}
                                />
                            )}

                            {analysisType === "somatic_tumor_normal" && (
                                <>
                                    <ControlSampleAndExperiment
                                        controlSample={controlSample}
                                        allDataApis={allDataApis}
                                    />
                                </>
                            )}

                            {analysisType === "germline" && (
                                <GermlineSampleAndExperiment
                                    selectedDir={selectedDir}
                                    allDataApis={allDataApis}
                                />
                            )}
                            <Observations
                                sampleQualityObservations={sampleQualityObservations}
                                sequencingQualityObservations={sequencingQualityObservations}
                                handleInputChange={handleInputChange}
                            /> 
                        {hideFooter && (
                        <Paper
                            elevation={3}
                            sx={{
                                position: "fixed",
                                bottom: 0,
                                left: 0,
                                right: 0,
                                height: "50px",
                                display: "flex",
                                justifyContent: "flex-end",
                                alignItems: "center",
                                zIndex: 1000
                            }}
                        >
                            <CaButton
                                onClick={handleSave} 
                                variant="contained"
                                text="Save"
                                size="large"
                                customStyles={{ width: "90px", marginRight:"20px"}}
                            />
                        </Paper>
                    )}

                       
                </Box>
            </Grid>
            </Grid>
            
        </Box>
        
    );
    }